/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Grid, IconButton, TextField, Typography } from '@material-ui/core';

import SearchIcon from '@material-ui/icons/Search';

import { useTranslation } from 'react-i18next';

import { useDispatch } from 'react-redux';

import useStyle from './style';

import WebhookLogFilterInputs from './webhook-log-filter-inputs';

import { PAGINATION_LIMIT, WebhookDateRange, WebhookLogDateRange, WebhookLogState } from 'types';
import { fetchWebhookHistoryApi } from 'store/features/developer/webhook/webhook.action';
import { getDateRange } from 'helpers/format.helper';
import Accordion from 'components/accordion/accordion';

interface Props {
  handleSelected?: (value) => void;
  componentClasses?: string;
}

export default function WebhookLogsFilters({
  handleSelected,
  componentClasses,
}: Props): JSX.Element {
  const classes = useStyle();
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const [connectionUuid, setConnectionUuid] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(WebhookLogState.ALL);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState(WebhookLogDateRange.LAST_SEVEN_DAYS);

  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  function handleWindowSizeChange(): void {
    setIsMobile(window.innerWidth <= 768);
  }
  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  const getFormattedDateRange = (dateRange: WebhookLogDateRange): WebhookDateRange => {
    let formattedDate;

    switch (dateRange) {
      case WebhookLogDateRange.TODAY:
        formattedDate = getDateRange(0, 0, 0, true);
        break;
      case WebhookLogDateRange.LAST_HOUR:
        formattedDate = getDateRange(0, 0, 1);
        break;
      case WebhookLogDateRange.LAST_SEVEN_DAYS:
        formattedDate = getDateRange(0, 7, 0);
        break;
      case WebhookLogDateRange.LAST_THIRTY_DAYS:
        formattedDate = getDateRange(0, 30, 0);
        break;
      case WebhookLogDateRange.LAST_NINETY_DAYS:
        formattedDate = getDateRange(0, 90, 0);
        break;
      case WebhookLogDateRange.LAST_SIX_MONTHS:
        formattedDate = getDateRange(6, 0, 0);
        break;
      default:
        break;
    }
    return formattedDate;
  };

  const handleApplyFilter = (): void => {
    const payload = {
      limit: PAGINATION_LIMIT,
      status: selectedStatus === WebhookLogState.ALL ? null : selectedStatus,
      webhookEvent: selectedEvent || null,
      connectionUuid: connectionUuid || null,
      startDate: null,
      endDate: null,
    };
    if (selectedDateRange) {
      const { startDate, endDate } = getFormattedDateRange(selectedDateRange);
      payload.startDate = startDate;
      payload.endDate = endDate;
    }
    handleSelected(payload);
    dispatch(fetchWebhookHistoryApi(payload));
  };

  const handleClearFilter = (): void => {
    setConnectionUuid('');
    setSelectedStatus(WebhookLogState.ALL);
    setSelectedEvent('');
    setSelectedDateRange(WebhookLogDateRange.LAST_SEVEN_DAYS);
    const { startDate, endDate } = getFormattedDateRange(WebhookLogDateRange.LAST_SEVEN_DAYS);
    const payload = {
      limit: PAGINATION_LIMIT,
      startDate,
      endDate,
    };
    handleSelected(payload);
    dispatch(fetchWebhookHistoryApi(payload));
  };

  const onStatusChange = (event): void => {
    setSelectedStatus(event.target.value);
  };

  const onEventChange = (event): void => {
    setSelectedEvent(event.target.value);
  };

  const onDateRangeChange = (event): void => {
    setSelectedDateRange(event.target.value);
  };

  const onConnectionUuidChange = (event): void => {
    setConnectionUuid(event.target.value);
  };

  return (
    <>
      <div data-testid="webhook-logs-filter" className={`${componentClasses}`}>
        <Grid container spacing={2} className={classes.wrapperFilter} alignItems="center">
          <Grid item lg={4} md={4} sm={12} xs={12} className={classes.statusWrapper}>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <IconButton className={classes.iconButton} aria-label="search">
                    <SearchIcon style={{ color: '#424242' }} />
                  </IconButton>
                ),
              }}
              placeholder={t('DASHBOARD_ENUM_WEBHOOK_LOG_CTA_SEARCH')}
              value={connectionUuid}
              onChange={onConnectionUuidChange}
              data-testid="search-input"
            />
          </Grid>
          {isMobile ? (
            <>
              <Accordion
                className={classes.root}
                header={
                  <span className={classes.header}>
                    <Typography variant="h3" className={classes.title}>
                      {'Filters'}
                    </Typography>
                  </span>
                }
                showAccordion={true}
                children={
                  <>
                    <WebhookLogFilterInputs
                      selectedDateRange={selectedDateRange}
                      onDateRangeChange={onDateRangeChange}
                      selectedStatus={selectedStatus}
                      onStatusChange={onStatusChange}
                      selectedEvent={selectedEvent}
                      onEventChange={onEventChange}
                      handleApplyFilter={handleApplyFilter}
                      handleClearFilter={handleClearFilter}
                    />
                  </>
                }
              />
            </>
          ) : (
            <WebhookLogFilterInputs
              selectedDateRange={selectedDateRange}
              onDateRangeChange={onDateRangeChange}
              selectedStatus={selectedStatus}
              onStatusChange={onStatusChange}
              selectedEvent={selectedEvent}
              onEventChange={onEventChange}
              handleApplyFilter={handleApplyFilter}
              handleClearFilter={handleClearFilter}
            />
          )}
        </Grid>
      </div>
    </>
  );
}
